import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import Button from '../ui/Button';
import { NavigationPreviousIcon, NavigationNextIcon, ZoomInIcon, ZoomOutIcon } from '../ui/Icons';
import { TIMELINE_VIEWPORT_WIDTH, ZOOM_LEVELS, TIME_WINDOWS } from '../../utils/constants';
import { formatTime } from '../../utils/timeUtils';

const ControlsContainer = styled.div`
  width: ${TIMELINE_VIEWPORT_WIDTH}px;
  height: 48px;
  background: ${theme.colors.surface};
  border-radius: 0 0 ${theme.borderRadius.lg} ${theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${theme.spacing.lg};
  margin-top: 0px;
  box-shadow: ${theme.shadows.sm};
  flex-shrink: 0;
  
  /* Responsive width for smaller screens */
  @media screen and (max-width: 1440px) {
    width: 960px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  width: 300px;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  width: 200px;
  justify-content: flex-end;
`;

const NavigationSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
`;

const ModeSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

const ZoomSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const SpeedSelect = styled.select`
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSizes.xs};
  font-family: ${theme.typography.fontFamily};
  padding: 2px 6px;
  height: 28px;
  cursor: pointer;
  outline: none;
  flex-shrink: 0;

  &:hover {
    border-color: ${theme.colors.primary};
  }

  option {
    background: ${theme.colors.surface};
  }
`;

const ZoomButton = styled(Button)`
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colors.background};
  border: 2px solid ${theme.colors.border};
  color: ${theme.colors.text};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.background};
    transform: scale(1.05);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const ZoomInfo = styled.span`
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.typography.fontWeights.normal};
  white-space: nowrap;
`;



const ZoomSlider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  background: ${theme.colors.borderLight};
  border-radius: 3px;
  outline: none;
  flex: 1;
  min-width: 80px;
  border: 1px solid ${theme.colors.border};
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 8px;
    height: 20px;
    background: ${theme.colors.primary};
    border-radius: 2px;
    cursor: pointer;
    border: 1px solid ${theme.colors.background};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    
    &:hover {
      background: ${theme.colors.primaryHover};
      transform: scale(1.1);
    }
  }
  
  &::-moz-range-thumb {
    width: 8px;
    height: 20px;
    background: ${theme.colors.primary};
    border-radius: 2px;
    cursor: pointer;
    border: 1px solid ${theme.colors.background};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    
    &:hover {
      background: ${theme.colors.primaryHover};
    }
  }
`;

const ZoomLevelDisplay = styled.span`
  font-size: ${theme.typography.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.typography.fontWeights.medium};
  min-width: 45px;
  text-align: center;
  font-family: ${theme.typography.fontFamily};
`;

const ToggleSwitch = styled.div`
  position: relative;
  width: 300px;
  height: 32px;
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.border};
  border-radius: 16px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px;
  
  &:hover {
    border-color: ${theme.colors.primaryHover};
  }
`;

const ToggleOption = styled.div`
  flex: 1;
  height: 100%;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  transition: all 0.3s ease;
  cursor: pointer;
  
  background: ${props => props.active ?
    (props.mode === 'court' ? theme.colors.courtBaseline :
     props.mode === 'player' ? theme.colors.playerMode :
     props.mode === 'rally' ? theme.colors.accent :
     theme.colors.primary) :
    'transparent'};
  color: ${props => props.active ?
    (props.mode === 'player' || props.mode === 'rally' ? theme.colors.background : theme.colors.text) :
    theme.colors.textMuted};

  &:hover {
    background: ${props => props.active ?
      (props.mode === 'court' ? theme.colors.courtBaseline :
       props.mode === 'player' ? theme.colors.playerMode :
       props.mode === 'rally' ? theme.colors.accent :
       theme.colors.primary) :
      theme.colors.surfaceHover};
  }
`;

const SPEED_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2];

const VideoControls = ({
  isPaused,
  onPlayPause,
  currentTime,
  videoDuration,
  annotations,
  courtKeypoints,
  playerRef,
  videoMode,
  isCourtMode,
  isPlayerMode,
  onToggleMode,
  onModeSelect,
  timelineZoom,
  onTimelineZoom,
  playbackRate = 1,
  onPlaybackRateChange,
}) => {
  // Build sorted event timestamps once when the source data changes (O(n)),
  // then use O(1) boundary checks and O(log n) binary search per frame.
  const sortedTimestamps = useMemo(() => {
    let timestamps;
    if (isCourtMode) {
      timestamps = (courtKeypoints || []).map(c => c.timestamp);
    } else {
      timestamps = annotations.filter(a => a.type !== 'in-flight').map(a => a.timestamp);
    }
    return timestamps.sort((a, b) => a - b);
  }, [annotations, courtKeypoints, isCourtMode, isPlayerMode]);

  const hasPreviousEvents = sortedTimestamps.length > 0 && sortedTimestamps[0] < currentTime;
  const hasNextEvents = sortedTimestamps.length > 0 && sortedTimestamps[sortedTimestamps.length - 1] > currentTime;

  const navigateToPrevious = useCallback(() => {
    if (!playerRef.current || sortedTimestamps.length === 0) return;
    // Binary search for the largest timestamp < currentTime
    let lo = 0, hi = sortedTimestamps.length - 1, best = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (sortedTimestamps[mid] < currentTime) { best = mid; lo = mid + 1; }
      else hi = mid - 1;
    }
    if (best >= 0) playerRef.current.currentTime(sortedTimestamps[best]);
  }, [sortedTimestamps, currentTime, playerRef]);

  const navigateToNext = useCallback(() => {
    if (!playerRef.current || sortedTimestamps.length === 0) return;
    // Binary search for the smallest timestamp > currentTime
    let lo = 0, hi = sortedTimestamps.length - 1, best = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (sortedTimestamps[mid] > currentTime) { best = mid; hi = mid - 1; }
      else lo = mid + 1;
    }
    if (best >= 0) playerRef.current.currentTime(sortedTimestamps[best]);
  }, [sortedTimestamps, currentTime, playerRef]);

  // Calculate the visible time window based on zoom
  const getTimeWindow = (zoom) => {
    const index = ZOOM_LEVELS.findIndex(level => level === zoom || (level !== null && Math.abs(level - zoom) < 0.01));
    if (index >= 0) {
      const timeWindow = TIME_WINDOWS[index];
      // If timeWindow is null, use the full video duration
      return timeWindow === null ? videoDuration : timeWindow;
    }
    return 15; // default to 15s for 1x
  };

  // Convert zoom level to slider value (0-100)
  const zoomToSliderValue = (zoom) => {
    if (zoom === null) return 0; // Full timeline
    const index = ZOOM_LEVELS.findIndex(level => level === zoom || (level !== null && Math.abs(level - zoom) < 0.01));
    return (index / (ZOOM_LEVELS.length - 1)) * 100;
  };

  // Convert slider value (0-100) to zoom level
  const sliderValueToZoom = (sliderValue) => {
    const index = Math.round((sliderValue / 100) * (ZOOM_LEVELS.length - 1));
    return ZOOM_LEVELS[index];
  };



  // Handle zoom in
  const handleZoomIn = () => {
    if (!videoDuration || !onTimelineZoom) return;
    
    // Find current zoom level index
    const currentIndex = ZOOM_LEVELS.findIndex(level => level === timelineZoom || (level !== null && Math.abs(level - timelineZoom) < 0.01));
    const newIndex = Math.min(ZOOM_LEVELS.length - 1, currentIndex + 1);
    const newZoom = ZOOM_LEVELS[newIndex];
    
    if (newZoom !== timelineZoom) {
      onTimelineZoom(newZoom);
    }
  };

  // Handle zoom out
  const handleZoomOut = () => {
    if (!videoDuration || !onTimelineZoom) return;
    
    // Find current zoom level index
    const currentIndex = ZOOM_LEVELS.findIndex(level => level === timelineZoom || (level !== null && Math.abs(level - timelineZoom) < 0.01));
    const newIndex = Math.max(0, currentIndex - 1);
    const newZoom = ZOOM_LEVELS[newIndex];
    
    if (newZoom !== timelineZoom) {
      onTimelineZoom(newZoom);
    }
  };

  return (
    <ControlsContainer data-tutorial="video-controls">
      <LeftSection>
        <ZoomInfo>Playback:</ZoomInfo>
        <SpeedSelect
          value={playbackRate}
          onChange={e => onPlaybackRateChange && onPlaybackRateChange(Number(e.target.value))}
          title="Playback speed"
        >
          {SPEED_OPTIONS.map(s => (
            <option key={s} value={s}>{s}x</option>
          ))}
        </SpeedSelect>
        <ZoomSection>
          <ZoomButton
            variant="default"
            onClick={handleZoomOut}
            disabled={timelineZoom === ZOOM_LEVELS[0]}
            title="Zoom Out"
          >
            <ZoomOutIcon />
          </ZoomButton>
          
          <ZoomButton
            variant="default"
            onClick={handleZoomIn}
            disabled={timelineZoom === ZOOM_LEVELS[ZOOM_LEVELS.length - 1]}
            title="Zoom In"
          >
            <ZoomInIcon />
          </ZoomButton>
          
          <ZoomInfo>
            {timelineZoom === null ? 'full timeline' : `${timelineZoom.toFixed(2)}x • ${formatTime(getTimeWindow(timelineZoom))} window`}
          </ZoomInfo>
        </ZoomSection>
      </LeftSection>
      
      <NavigationSection>
        <Button
          variant="navigation"
          onClick={navigateToPrevious}
          disabled={!hasPreviousEvents}
          title="Previous Event"
        >
          <NavigationPreviousIcon />
        </Button>
        
        <Button
          variant="navigation"
          onClick={navigateToNext}
          disabled={!hasNextEvents}
          title="Next Event"
        >
          <NavigationNextIcon />
        </Button>
      </NavigationSection>
      
      <RightSection>
        <ModeSection>
          <ToggleSwitch title="Select Mode" data-tutorial="mode-toggle">
            <ToggleOption 
              active={videoMode === 'ball'} 
              mode="ball"
              onClick={(e) => {
                e.stopPropagation();
                onModeSelect('ball');
              }}
            >
              Ball
            </ToggleOption>
            <ToggleOption 
              active={videoMode === 'player'} 
              mode="player"
              onClick={(e) => {
                e.stopPropagation();
                onModeSelect('player');
              }}
            >
              Player
            </ToggleOption>
            <ToggleOption 
              active={videoMode === 'court'} 
              mode="court"
              onClick={(e) => {
                e.stopPropagation();
                onModeSelect('court');
              }}
            >
              Court
            </ToggleOption>
            <ToggleOption 
              active={videoMode === 'rally'} 
              mode="rally"
              onClick={(e) => {
                e.stopPropagation();
                onModeSelect('rally');
              }}
            >
              Rally
            </ToggleOption>
          </ToggleSwitch>
        </ModeSection>
      </RightSection>
    </ControlsContainer>
  );
};

export default React.memo(VideoControls);